import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthGuardSkeleton } from './ui/AppSkeleton';
import { appRoutes } from '../constants/routes';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string | string[];
}

export default function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, checkAuthStatus, hcpApprovalLogin } = useAuth();
  const location = useLocation();
  const [isApprovalAutoLoginLoading, setIsApprovalAutoLoginLoading] = useState(false);
  const attemptedApprovalLoginRef = useRef<string | null>(null);

  const approvalContext = useMemo(() => {
    const match = location.pathname.match(/^\/programs\/([^/]+)\/hcp-dashboard\/(patient-approvals|patient-approval-review)$/i);
    if (!match) {
      return null;
    }

    const searchParams = new URLSearchParams(location.search);
    const approvalToken = searchParams.get('approvalToken');
    const patientId = searchParams.get('patientId');
    if (!approvalToken || !patientId) {
      return null;
    }

    return {
      programId: match[1],
      patientId,
      approvalToken,
      attemptKey: `${match[1]}:${patientId}:${approvalToken}`,
    };
  }, [location.pathname, location.search]);

  // Check auth status on every render to catch expired tokens
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus, location.pathname]);

  useEffect(() => {
    if (isLoading || isAuthenticated || !approvalContext) {
      return;
    }

    if (attemptedApprovalLoginRef.current === approvalContext.attemptKey) {
      return;
    }

    attemptedApprovalLoginRef.current = approvalContext.attemptKey;
    setIsApprovalAutoLoginLoading(true);

    void hcpApprovalLogin(
      approvalContext.programId,
      approvalContext.patientId,
      approvalContext.approvalToken,
    ).finally(() => {
      setIsApprovalAutoLoginLoading(false);
    });
  }, [approvalContext, hcpApprovalLogin, isAuthenticated, isLoading]);

  // Prevent browser back button from showing cached authenticated pages
  useEffect(() => {
    // Push a new history entry and handle popstate
    const handlePopState = () => {
      // Re-check authentication when user navigates with back/forward buttons
      if (!checkAuthStatus()) {
        // Force navigation to login if not authenticated
        window.location.replace(appRoutes.login());
      }
    };

    // popstate only fires for same-document (SPA) history changes. When the
    // whole document is restored from the browser's back/forward cache
    // (bfcache) after full navigation away and back, no React effect reruns
    // and popstate does not fire — only pageshow with persisted=true does.
    // Without this, a bfcache-restored page can show stale authenticated UI
    // after logout/expiry/revocation until the next API call happens to fail.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted && !checkAuthStatus()) {
        window.location.replace(appRoutes.login());
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [checkAuthStatus]);

  // Add cache control meta tags to prevent caching of protected pages
  useEffect(() => {
    // Set cache control headers via meta tags
    const metaCacheControl = document.querySelector('meta[http-equiv="Cache-Control"]');
    const metaPragma = document.querySelector('meta[http-equiv="Pragma"]');
    const metaExpires = document.querySelector('meta[http-equiv="Expires"]');

    if (!metaCacheControl) {
      const meta = document.createElement('meta');
      meta.httpEquiv = 'Cache-Control';
      meta.content = 'no-cache, no-store, must-revalidate';
      document.head.appendChild(meta);
    }

    if (!metaPragma) {
      const meta = document.createElement('meta');
      meta.httpEquiv = 'Pragma';
      meta.content = 'no-cache';
      document.head.appendChild(meta);
    }

    if (!metaExpires) {
      const meta = document.createElement('meta');
      meta.httpEquiv = 'Expires';
      meta.content = '0';
      document.head.appendChild(meta);
    }
  }, []);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background px-6 py-4">
        <AuthGuardSkeleton message="Verifying session..." className="mx-auto max-w-[1400px]" />
      </div>
    );
  }

  if (!isAuthenticated && approvalContext && (isApprovalAutoLoginLoading || attemptedApprovalLoginRef.current !== approvalContext.attemptKey)) {
    return (
      <div className="min-h-screen bg-background px-6 py-4">
        <AuthGuardSkeleton message="Signing you in from approval link..." className="mx-auto max-w-[1400px]" />
      </div>
    );
  }

  if (!isAuthenticated && isApprovalAutoLoginLoading) {
    return (
      <div className="min-h-screen bg-background px-6 py-4">
        <AuthGuardSkeleton message="Signing you in from approval link..." className="mx-auto max-w-[1400px]" />
      </div>
    );
  }

  if (isAuthenticated && approvalContext) {
    const storageKey = `epprem_approval_token:${approvalContext.programId}:${approvalContext.patientId}`;
    sessionStorage.setItem(storageKey, approvalContext.approvalToken);
    const sanitized = `${location.pathname}?patientId=${encodeURIComponent(approvalContext.patientId)}`;
    return <Navigate to={sanitized} replace />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to={appRoutes.login()} state={{ from: location }} replace />;
  }

  // Check role requirements if specified
  if (requiredRole && user) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!roles.includes(user.role)) {
      // User doesn't have the required role, redirect to dashboard or unauthorized page
      return <Navigate to={appRoutes.unauthorized()} replace />;
    }
  }

  return <>{children}</>;
}
